import React from "react";
import { useTranslation } from "react-i18next";
import SearchResultItem from "./SearchResultItem";
import type { TransformedProduct } from "../../../../product/types/productApiTypes";

interface SearchResultListProps {
  results: TransformedProduct[] | undefined;
  isFetching: boolean; // Still indicates if *any* fetch is active
  isError: boolean;
  searchTerm: string; // The immediate term typed by the user
  debouncedSearchTerm: string; // The term last used for fetching
  onItemClick?: () => void;
}

const SearchResultList: React.FC<SearchResultListProps> = ({
  results,
  isFetching,
  isError,
  searchTerm,
  debouncedSearchTerm,
  onItemClick,
}) => {
  const { t } = useTranslation();
  const hasResults = results && results.length > 0;
  const immediateTermIsTooShort = searchTerm.trim().length <= 2;
  // Check if the term used for the last fetch is valid length
  const debouncedTermWasValidLength = debouncedSearchTerm.trim().length > 2;
  // Check if user is potentially typing (terms mismatch)
  const isTypingOrDebouncing = searchTerm !== debouncedSearchTerm;

  // 1. Handle Error First
  if (isError) {
    return (
      <div className="search-result-message error">
        {t("searchPanel.errorLoadingResults")}
      </div>
    );
  }

  // 2. Handle "Enter Search Term" if immediate term is too short AND not fetching
  if (immediateTermIsTooShort && !isFetching) {
    return (
      <div className="search-result-message">
        {t("searchPanel.enterSearchTerm")}
      </div>
    );
  }

  // 3. Handle No Results - *** REFINED CONDITION ***
  // Show only if:
  // - Fetching is complete for the debounced term
  // - That fetch yielded no results
  // - The debounced term was actually long enough to be valid
  // - The user isn't currently typing something different (terms match)
  // - AND we're not in a transition state where results are being updated
  const showNoResults =
    !isFetching &&
    !hasResults &&
    debouncedTermWasValidLength &&
    !isTypingOrDebouncing &&
    results !== undefined; // Only show no results when results array is actually defined (not in initial/loading state)

  if (showNoResults) {
    return (
      <div className="search-result-message no-results">
        {/* Show message based on the term that *was* searched */}
        {t("searchPanel.noResults", { term: debouncedSearchTerm })}
      </div>
    );
  }

  // 4. Display Results (or nothing during fetch/typing)
  // Parent overlay handles visual loading. List shows results when ready and terms match.
  // If isTypingOrDebouncing is true, we won't show "No Results", and might show stale results briefly, which is fine.
  return (
    <ul className="search-result-list">
      {/* Display results if available */}
      {hasResults &&
        results.map((product) => (
          <SearchResultItem
            key={product.id}
            product={product}
            onItemClick={onItemClick}
          />
        ))}
    </ul>
  );
};

export default SearchResultList;
